import type { SpreadBackgroundGeometry } from '@/components/app/backgrounds/spread-background-layer'
import type { PageViewMode } from '@/components/pages/home'
import type { GridSnapLiveRect } from '@/lib/guides/grid-snap'

/** Visible document size in canvas space (one page in single-page view, full spread otherwise). */
export function computeFitContentDimensions({
  isSinglePage,
  bleedWidth,
  bleedHeight,
  singlePageBleedWidth,
}: {
  isSinglePage: boolean
  bleedWidth: number
  bleedHeight: number
  singlePageBleedWidth: number
}): { width: number; height: number } {
  const width = isSinglePage ? Math.max(1, singlePageBleedWidth) : Math.max(1, bleedWidth)
  const height = Math.max(1, bleedHeight)

  return { width, height }
}

/** Which page of a single-page spread is real (`null` for a normal two-page spread). */
export function resolveSinglePageSide(
  pageViewMode: PageViewMode
): SpreadBackgroundGeometry['singlePageSide'] {
  return pageViewMode === 'single-first' ? 'first' : pageViewMode === 'single-last' ? 'last' : null
}

/** True for the first page of a single-page view (the real page is the spread's right half). */
export function isSingleFirstPage(pageViewMode: PageViewMode): boolean {
  return pageViewMode === 'single-first'
}

/** Which half of the spread a single-page view's real page sits on: the first page on the right. */
export function resolveSinglePageSpreadSide(pageViewMode: PageViewMode): 'left' | 'right' {
  return isSingleFirstPage(pageViewMode) ? 'right' : 'left'
}

/** Matches tiled grid: each half uses the snapped seam so snap-to-grid matches painted lines. */
export function computeGridSnapLiveRects({
  pageViewMode,
  pageTrimWidth,
  bleedMargin,
  bleedHeight,
  snappedSpreadOriginCanvasX,
  singlePageBleedWidth,
}: {
  pageViewMode: PageViewMode
  pageTrimWidth: number
  bleedMargin: number
  bleedHeight: number
  snappedSpreadOriginCanvasX: number
  singlePageBleedWidth: number
}): GridSnapLiveRect[] {
  const spineX = snappedSpreadOriginCanvasX

  if (pageTrimWidth <= 0 || bleedHeight <= 0) {
    return []
  }

  if (pageViewMode === 'single-first') {
    return [
      {
        x: spineX - bleedMargin,
        y: 0,
        width: singlePageBleedWidth,
        height: bleedHeight,
      },
    ]
  }

  if (pageViewMode === 'single-last') {
    return [{ x: 0, y: 0, width: singlePageBleedWidth, height: bleedHeight }]
  }

  const halfW = bleedMargin + pageTrimWidth

  return [
    { x: 0, y: 0, width: halfW, height: bleedHeight },
    { x: spineX, y: 0, width: halfW, height: bleedHeight },
  ]
}

/**
 * Full spread width in canvas X (single-first / single-last); clips horizontal guides + smart
 * guides so they don't bleed out into the far, unrelated pasteboard beyond the spread
 * (WMPCM-1944). Spans the WHOLE spread — including the cover/no-page side — not just the real
 * page's own width: a vertical guide already spans the full height unconditionally on a
 * single-page spread, so a horizontal guide should reach the cover side the same way, just
 * stopping at the spread's own edge rather than running out across all 6000pt of pasteboard
 * (WMPCM-10229). Anchored at the real page's known edge (the trusted, already-correct value)
 * and widened outward by `bleedWidth` (the full two-page spread's bleed width) rather than
 * `singlePageBleedWidth` (one page's width only).
 */
export function computeSinglePageHorizontalGuideSpan({
  isSinglePage,
  pageViewMode,
  bleedMargin,
  bleedWidth,
  pageTrimWidth,
  snappedSpreadOriginCanvasX,
  singlePageBleedWidth,
}: {
  isSinglePage: boolean
  pageViewMode: PageViewMode
  bleedMargin: number
  bleedWidth: number
  pageTrimWidth: number
  snappedSpreadOriginCanvasX: number
  singlePageBleedWidth: number
}): { left: number; width: number } | null {
  if (!isSinglePage) return null

  if (pageTrimWidth <= 0 || singlePageBleedWidth <= 0 || bleedWidth <= 0) return null

  if (pageViewMode === 'single-first') {
    const realPageRightEdge = snappedSpreadOriginCanvasX - bleedMargin + singlePageBleedWidth

    return {
      left: realPageRightEdge - bleedWidth,
      width: bleedWidth,
    }
  }

  if (pageViewMode === 'single-last') {
    return { left: 0, width: bleedWidth }
  }

  return null
}
